<script setup lang="ts">
import type { RegionSelectProps } from '.';
import type { AddressData } from '~~/core/entities/Addresses';

import { useEvents } from '~/composables/events/useEvents';
import { useAddresses } from '~/composables/useAddressesWrapper';
import { useHighlightedRegions } from '~/composables/useHighlightedRegionsWrapper';
import { MixpanelProps } from '~/core/service/mixpanel';
import { AddressUtils } from '~/utils/AddressUtilsWrapper';
import { getCityFromAddressSlug, highlightParts, isNumber } from '~~/utils';

import { DefaultFilterSelect } from '../DefaultFilterSelect/';
import messages from './RegionSelect.locale.json';

type AddressDataWithUniqueKey = AddressData & { uniqueKey: string };

const { searchPropertyEvents } = useEvents();
const { getHighlightedRegions } = useHighlightedRegions();

const { t } = useI18n({
	useScope: 'local',
	messages
});

const props = withDefaults(defineProps<RegionSelectProps>(), {
	regions: () => []
});
const emit = defineEmits<{
	(
		e: 'update-selection',
		payload: {
			regions: string[];
			states: string[];
			cities: string[];
		}
	): void;
	(e: 'selected-city', payload: string): void;
}>();

// Cache normalizado de regiões para melhor performance
const regionsMapByCity = computed(() => {
	const map = new Map<string, AddressData>();
	regions.value.forEach(region => {
		if (region.data.city === props.city) {
			map.set(normalizeString(region.label), region);
		}
	});
	return map;
});

const regionsMap = computed(() => {
	const map = new Map<string, AddressData>();
	regions.value.forEach(region => {
		const key = normalizeString(region.label);
		if (!map.has(key)) {
			map.set(key, region);
		}
	});
	return map;
});

function findRegionByName(regionName: string): AddressData | undefined {
	const normalizedName = normalizeString(regionName);

	if (props.city) {
		const foundInCity = regionsMapByCity.value.get(normalizedName);
		if (foundInCity) return foundInCity;
	}

	return regionsMap.value.get(normalizedName);
}

function createFallbackRegion(regionName: string): AddressData {
	return {
		label: regionName,
		data: {
			city: props.city || AddressUtils.getDefaultCityForState(state.value),
			macroregion: props.macroregion || ''
		}
	} as AddressData;
}

const localSelectedRegions = computed<AddressDataWithUniqueKey[]>(() => {
	return props.regions.map(regionName => {
		const found =
			findRegionByName(regionName) || createFallbackRegion(regionName);
		return {
			...found,
			uniqueKey: `${found.label}-${found.data.city}-${found.data.state || 'SP'}`
		};
	});
});

interface DefaultFilterSelectExposed {
	searchTerm: string;
}

const selectRef = useTemplateRef<DefaultFilterSelectExposed>('selectRef');

const searchTerm = ref('');

watch(
	() => selectRef.value?.searchTerm,
	newValue => {
		if (newValue !== undefined) {
			searchTerm.value = newValue;
		}
	},
	{ immediate: true }
);

const {
	regions,
	filteredRegions,
	state: defaultState,
	addresses
} = useAddresses(searchTerm);

// Determina se os dados de endereços ainda estão carregando
const isLoadingAddresses = computed(() => {
	return addresses.value.length === 0;
});

const city = computed(() => {
	if (props.city) return props.city;
	return getCityFromAddressSlug(props.addressSlug) || '';
});

const state = computed(() => {
	// Prioridade 1: Detectar estado pela cidade (props ou extraída do addressSlug)
	if (city.value) {
		const detectedState = AddressUtils.getStateByCity(city.value);

		if (detectedState) {
			return detectedState;
		}
	}

	// Prioridade 2: Detectar estado pela macrorregião
	if (props.macroregion) {
		const macroregionContext = AddressUtils.getMacroregionContext(
			defaultState.value,
			props.macroregion
		);
		if (macroregionContext?.state) {
			return macroregionContext.state;
		}
	}

	// Prioridade 3: Usar estado explícito dos filtros
	if (props.state) {
		return props.state;
	}

	// Fallback: usar estado padrão
	return defaultState.value;
});

// Memoize normalized city to avoid redundant normalization in filters
const normalizedCityFromProps = computed(() =>
	props.city ? normalizeString(props.city) : null
);

const filterByCityOrMacroregion = (region: AddressData): boolean => {
	const hasAddressSlug = Boolean(props.addressSlug);

	if (hasAddressSlug) {
		const addressSlugCity =
			getCityFromAddressSlug(props.addressSlug) ||
			props.city ||
			AddressUtils.getDefaultCityForState(state.value);

		const slugifiedCity = normalizeString(addressSlugCity);
		const slugifiedRegionCity = normalizeString(region.data.city);

		return slugifiedCity === slugifiedRegionCity;
	}

	if (normalizedCityFromProps.value) {
		const normalizedRegionCity = normalizeString(region.data.city);

		return normalizedRegionCity === normalizedCityFromProps.value;
	}

	if (props.macroregion) {
		return region.data.macroregion === props.macroregion;
	}

	return false;
};

const uniqueFilteredRegions = computed(() => {
	const allRegions = [...filteredRegions.value];

	const filteredByStateAndLocation = allRegions.filter(region => {
		if (region.data.state && region.data.state !== state.value) {
			return false;
		}
		return filterByCityOrMacroregion(region);
	});

	const uniqueRegions = new Map<string, AddressData>();
	filteredByStateAndLocation.forEach(region => {
		const key = `${normalizeString(region.label)}-${normalizeString(region.data.city)}`;
		if (!uniqueRegions.has(key)) {
			uniqueRegions.set(key, region);
		}
	});

	return Array.from(uniqueRegions.values());
});

const items = computed<AddressData[]>(() => {
	return uniqueFilteredRegions.value.slice(0, 50);
});

const itemsWithUniqueKeys = computed<AddressDataWithUniqueKey[]>(() => {
	return items.value.map(item => ({
		...item,
		uniqueKey: `${item.label}-${item.data.city}-${item.data.state || 'SP'}`
	}));
});

async function waitForFetchingToComplete() {
	return await until(() => !props.isFetchingProperties).toBeTruthy();
}

const awaitForFetchingToStart = async () => {
	return await until(() => props.isFetchingProperties).toBeTruthy();
};

const waitForTotalResults = async () => {
	return await until(() => isNumber(props.countProperties)).toBeTruthy();
};

async function handleClickHighlightItem({
	item,
	index
}: {
	item: AddressData;
	index: number;
}) {
	await awaitForFetchingToStart();
	await Promise.all([waitForFetchingToComplete(), waitForTotalResults()]);

	handleEventHighlightItemClick(item, index);
}

const handleEventHighlightItemClick = (item: AddressData, index: number) => {
	const otherSuggestions = highlightedItems.value
		.reduce((labels: string[], suggestion) => {
			if (suggestion.label !== item.label) {
				labels.push(suggestion.label);
			}
			return labels;
		}, [])
		.join(', ');

	searchPropertyEvents.trackSuggestionFilter({
		[MixpanelProps.REGION_NAME]: item.data.macroregion,
		[MixpanelProps.CITY_NAME]: item.data.city,
		[MixpanelProps.SUGGESTION_CLICKED]: item.label,
		[MixpanelProps.OTHER_SUGGESTIONS]: otherSuggestions,
		[MixpanelProps.SUGGESTION_POSITION]: index + 1,
		[MixpanelProps.TOTAL_RESULTS]: props.countProperties || 0
	});
};

const highlightedItems = computed<AddressDataWithUniqueKey[]>(() => {
	const items = getHighlightedRegions(
		state.value,
		props.city,
		props.macroregion,
		props.regions
	);

	return items.map(item => ({
		...item,
		uniqueKey: `${item.label}-${item.data.city}-${item.data.state || 'SP'}`
	}));
});

function getItemLabel(value: AddressData): string {
	return value.label;
}

function getSubText(item: AddressData): string {
	const city = item.data.city;
	const stateCode = item.data.state || 'SP';

	return `${city} - ${stateCode}`;
}

const filteredRegionsMap = computed(
	() =>
		new Map(items.value.map(region => [normalizeString(region.label), region]))
);

// Mapa para converter uniqueKey de volta para AddressData
const uniqueKeyToRegionMap = computed(() => {
	const map = new Map<string, AddressData>();
	uniqueFilteredRegions.value.forEach(region => {
		const key = `${region.label}-${region.data.city}-${region.data.state || 'SP'}`;
		map.set(key, region);
	});
	return map;
});

function updateSelectedRegions(
	newRegions: (string | AddressDataWithUniqueKey)[]
) {
	const mappedRegions = newRegions.map(region => {
		if (typeof region === 'string') {
			const found = uniqueKeyToRegionMap.value.get(region);
			if (found) {
				return found;
			}
			const foundByLabel = filteredRegionsMap.value.get(
				normalizeString(region)
			);
			return foundByLabel || createFallbackRegion(region);
		}

		return {
			label: region.label,
			data: {
				city: region.data.city,
				macroregion: region.data.macroregion,
				state: region.data.state
			}
		} satisfies AddressData;
	});

	const mappedNewRegions = mappedRegions.map(region => region.label);

	const citySet = new Set<string>();
	mappedRegions.forEach(region => {
		if (region.data.city) {
			citySet.add(region.data.city);
		}
	});

	const cities = Array.from(citySet);

	if (cities.length === 0) {
		const fallbackCity =
			props.city || AddressUtils.getDefaultCityForState(state.value);
		if (fallbackCity) cities.push(fallbackCity);
	}

	emit('update-selection', {
		regions: mappedNewRegions,
		states: [state.value],
		cities
	});
}
</script>

<template>
	<DefaultFilterSelect
		ref="selectRef"
		:model-value="localSelectedRegions"
		:title="t('title')"
		:items="itemsWithUniqueKeys"
		:highlighted-items="highlightedItems"
		:get-item-label="getItemLabel"
		:separator-variant="separatorVariant"
		:is-loading="isLoadingAddresses"
		object-value
		value-prop="uniqueKey"
		@update:model-value="updateSelectedRegions"
		@click-highlight-item="handleClickHighlightItem"
	>
		<template #item="{ item, searchTerm: localSearch }">
			<div class="flex flex-col items-start">
				<p class="text-start text-base font-normal leading-normal text-primary">
					<template
						v-for="(part, index) in highlightParts(item.label, localSearch)"
						:key="index"
					>
						<strong v-if="part.highlight" class="font-semibold">{{
							part.text
						}}</strong>
						<span v-else>{{ part.text }}</span>
					</template>
				</p>
				<small
					class="text-start text-xs font-normal leading-normal text-primary-light-2"
				>
					<template
						v-for="(part, index) in highlightParts(
							getSubText(item),
							localSearch
						)"
						:key="index"
					>
						<strong v-if="part.highlight" class="font-semibold">{{
							part.text
						}}</strong>
						<span v-else>{{ part.text }}</span>
					</template>
				</small>
			</div>
		</template>
		<template #selected-item-badge="{ item }">
			{{ item.label }}
		</template>
		<template #highlighted-item-badge="{ item }">
			{{ item.label }}
		</template>
	</DefaultFilterSelect>
</template>
